import { useMemo } from 'react';
import { useMaerkeDage } from './useMaerkeDage';
import { useFoedselsDage } from './useFoedselsDage';
import { resolverAlleOfficielle, resolverBrugerBegivenheder } from '../utils/dateUtils';
import { KalenderBegivenhed } from '../types';

interface UseUpcomingResult {
  upcoming: KalenderBegivenhed[];
  loading: boolean;
  error: string | null;
}

export function useUpcoming(maxDage: number = 60): UseUpcomingResult {
  const { maerkeDage, loading: loadingMD, error: errorMD } = useMaerkeDage();
  const { foedselsDage, loading: loadingFD, error: errorFD } = useFoedselsDage();

  const loading = loadingMD || loadingFD;
  const error = errorMD || errorFD;

  const upcoming = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const nextYear = currentYear + 1;

    // Collect events for current year and next year
    const officielleCurrentYear = resolverAlleOfficielle(currentYear);
    const officielleNextYear = resolverAlleOfficielle(nextYear);

    const brugerCurrentYear = resolverBrugerBegivenheder(maerkeDage, foedselsDage, currentYear);
    const brugerNextYear = resolverBrugerBegivenheder(maerkeDage, foedselsDage, nextYear);

    const all: KalenderBegivenhed[] = [
      ...officielleCurrentYear,
      ...officielleNextYear,
      ...brugerCurrentYear,
      ...brugerNextYear,
    ];

    // Filter to events within the next maxDage days (dageVaek >= 0 && dageVaek <= maxDage)
    const filtered = all.filter((b) => b.dageVaek >= 0 && b.dageVaek <= maxDage);

    // Sort by dageVaek ascending
    filtered.sort((a, b) => a.dageVaek - b.dageVaek);

    return filtered;
  }, [maerkeDage, foedselsDage, maxDage]);

  return { upcoming, loading, error };
}

/**
 * Get all events for a specific month/year combination (for calendar use).
 */
export function useBegivenhederForMaaned(aar: number, maaned: number): UseUpcomingResult {
  const { maerkeDage, loading: loadingMD, error: errorMD } = useMaerkeDage();
  const { foedselsDage, loading: loadingFD, error: errorFD } = useFoedselsDage();

  const loading = loadingMD || loadingFD;
  const error = errorMD || errorFD;

  const upcoming = useMemo(() => {
    const officielle = resolverAlleOfficielle(aar);
    const bruger = resolverBrugerBegivenheder(maerkeDage, foedselsDage, aar);
    const all = [...officielle, ...bruger];
    // Filter to the specific month (0-indexed)
    return all.filter((b) => b.dato.getMonth() === maaned);
  }, [maerkeDage, foedselsDage, aar, maaned]);

  return { upcoming, loading, error };
}
